import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import KeyValueTable from "../Common/KeyValueTable";
import MonacoEditorWithFullscreen from "../Common/MonacoEditorWithFullscreen";
import coreUtils from "../../utils/coreUtils";
import CollapsibleCodeBlock from "components/Common/CollapsibleCodeBlock";

const EntityResponse = ({ response }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    
    // Use ResizeObserver for more accurate container width changes
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  const options = useMemo(() => [
    { label: 'Request Body', value: 0 },
    { label: 'Request Headers', value: 1 },
    { label: 'Response Body', value: 2 },
    { label: 'Response Headers', value: 3 }
  ], []);

  const [selectedOption, setSelectedOption] = useState(0);

  const isJsonString = useCallback((str) => {
    if (typeof str !== 'string') return false;
    try {
      const parsed = JSON.parse(str);
      // Return true only for objects and arrays - treat primitives as plain text
      // This handles edge cases like: "null", "undefined", "true", "false", "123", '"string"'
      return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
      return false;
    }
  }, []);

  const formatJsonValue = useCallback((value) => {
    try {
      // If value is already a string, try to parse it first
      const jsonObj = typeof value === 'string' ? JSON.parse(value) : value;
      return JSON.stringify(jsonObj, null, 2);
    } catch (e) {
      // If parsing fails, return the original value or empty object
      return typeof value === 'string' ? value : '{}';
    }
  }, []);

  const renderResponseHeaders = useCallback(() => {
    let headerItems = [];
    const headers = response?.headers;
    if (headers) {
      headerItems = coreUtils.parseObjectToKeyValueArray(headers);
    }
    return <KeyValueTable allItems={headerItems} json={true} sxCell={{ textAlign: "left" }} isReadOnly />;
  }, [response?.headers]);

  const renderRequestBody = useCallback(() => {
    const requestBody = response?.request_body || "{}";
    return (
      <MonacoEditorWithFullscreen
        value={formatJsonValue(requestBody)}
        language="json"
        height="250px"
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          formatOnPaste: true,
          formatOnType: true
        }}
      />
    );
  }, [response?.request_body, formatJsonValue]);

  const renderRequestHeaders = useCallback(() => {
    let requestHeaderItems = [];
    const requestHeaders = response?.request_headers;
    if (requestHeaders) {
      requestHeaderItems = coreUtils.parseObjectToKeyValueArray(requestHeaders);
    }
    return <KeyValueTable allItems={requestHeaderItems} json={true} sxCell={{ textAlign: "left" }} isReadOnly />;
  }, [response?.request_headers]);

  const renderResponseBody = useCallback(() => {
    const rawBody = response?.body || "{}";
    const isJson = typeof rawBody === 'string' && isJsonString(rawBody);

    return (
      <div className="border !border-[var(--borderColor)] rounded-md">
        {isJson ? (
          <MonacoEditorWithFullscreen
            height="250px"
            options={{
              readOnly: true,
              formatOnPaste: true,
              formatOnType: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true
            }}
            language="json"
            value={formatJsonValue(rawBody)}
          />
        ) : (
          <CollapsibleCodeBlock
            content={String(typeof rawBody === 'string' ? rawBody : JSON.stringify(rawBody, null, 2))}
            language="html"
          />
        )}
      </div>
    );
  }, [response?.body, isJsonString, formatJsonValue]);

  const renderContent = useCallback(() => {
    switch (selectedOption) {
      case 3: // Res Headers
        return renderResponseHeaders();
      case 0: // Req Body
        return renderRequestBody();
      case 1: // Req Headers
        return renderRequestHeaders();
      default: // Res Body
        return renderResponseBody();
    }
  }, [selectedOption, renderResponseHeaders, renderRequestBody, renderRequestHeaders, renderResponseBody]);

  return (
    <Box sx={{ py: 2 }} ref={containerRef}>
      <div className="card bg-transparent">
        <Tabs
          value={selectedOption}
          onChange={(e, newValue) => setSelectedOption(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'var(--borderColor)',
            '& .MuiTab-root': {
              color: 'var(--text-secondary)',
              '&.Mui-selected': {
                color: 'var(--primary-color)',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--primary-color)',
            },
          }}
        >
          {options.map((option) => (
            <Tab
              key={option.value}
              label={option.label}
              value={option.value}
              sx={{
                px: containerWidth < 360 ? 0.5 : 0.75,
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'capitalize',
                color: 'var(--text-secondary)',
                '&.Mui-selected': {
                  color: 'var(--primary-color)',
                },
                maxWidth: containerWidth < 360 ? "80px" : "130px",
                minWidth: 'auto',
                whiteSpace: 'normal',
                wordBreak: "break-word",
                '&.MuiTab-root': {
                  minWidth: 'auto !important',
                  maxWidth: containerWidth < 360 ? '80px !important' : '130px !important',
                }
              }}
            />
          ))}
        </Tabs>
      </div>
      <Box sx={{ height: "100%", overflow: "auto" }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default EntityResponse;
